/**
 * GroupEditModal.jsx - Fleet Premium
 *
 * 2ECOMP: PremiumUI Refresh — modal para criar/editar grupo
 *
 * Props:
 *   open      — boolean, controla visibilidade
 *   onClose   — callback ao fechar
 *   group     — { id, name } para editar, null para criar
 *
 * Funcionalidades:
 *   - Nome do grupo (input)
 *   - Grid de devices com checkbox multi-select
 *   - Busca por nome de device
 *   - Select all / Deselect all
 *   - Batch PUT ao salvar (atualiza groupId dos devices)
 */

import {
  useState, useMemo, useCallback, useEffect,
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Checkbox,
  InputAdornment, IconButton,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import SelectAll from '@mui/icons-material/SelectAll';
import Deselect from '@mui/icons-material/Deselect';
import Close from '@mui/icons-material/Close';
import { devicesActions } from '../../store';
import { groupsActions } from '../../store/groups';
import { useTranslation } from '../../common/components/LocalizationProvider';

// 2ECOMP: PremiumUI Refresh — altura fixa da grid de devices
const DEVICE_ITEM_HEIGHT = 36;
const MAX_VISIBLE_DEVICES = 10;

const GroupEditModal = ({ open, onClose, group }) => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const isEdit = !!group;

  // Redux: todos os devices
  const allDevices = useSelector((state) => state.devices.items, shallowEqual);

  // State local
  const [groupName, setGroupName] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset state quando o modal abre
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSaving(false);

    if (isEdit) {
      setGroupName(group.name);
      // Selecionar devices que já pertencem ao grupo
      const ids = new Set();
      Object.values(allDevices).forEach((d) => {
        if (d.groupId === group.id) ids.add(d.id);
      });
      setSelectedDeviceIds(ids);
    } else {
      setGroupName('');
      setSelectedDeviceIds(new Set());
    }
  }, [open, isEdit, group, allDevices]);

  // Lista de devices ordenada e filtrada por busca
  const deviceList = useMemo(() => {
    const list = Object.values(allDevices).sort((a, b) => a.name.localeCompare(b.name));
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((d) => d.name.toLowerCase().includes(q));
  }, [allDevices, search]);

  // Toggle device individual
  const toggleDevice = useCallback((deviceId) => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
      }
      return next;
    });
  }, []);

  // Select all (filtered)
  const handleSelectAll = useCallback(() => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      deviceList.forEach((d) => next.add(d.id));
      return next;
    });
  }, [deviceList]);

  // Deselect all (filtered)
  const handleDeselectAll = useCallback(() => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      deviceList.forEach((d) => next.delete(d.id));
      return next;
    });
  }, [deviceList]);

  // Salvar: criar ou editar grupo + batch PUT devices
  const handleSave = useCallback(async () => {
    const trimmed = groupName.trim();
    if (!trimmed) return;
    setSaving(true);

    try {
      let targetGroupId;

      if (isEdit) {
        // PUT grupo (rename)
        const response = await fetch(`/api/groups/${group.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...group, name: trimmed }),
        });
        if (!response.ok) { setSaving(false); return; }
        const updated = await response.json();
        dispatch(groupsActions.update(updated));
        targetGroupId = group.id;
      } else {
        // POST novo grupo
        const response = await fetch('/api/groups', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmed }),
        });
        if (!response.ok) { setSaving(false); return; }
        const created = await response.json();
        dispatch(groupsActions.update(created));
        targetGroupId = created.id;
      }

      // Batch PUT: atualizar groupId dos devices
      const allDeviceList = Object.values(allDevices);
      const updates = [];

      allDeviceList.forEach((device) => {
        const wasInGroup = isEdit && device.groupId === group.id;
        const shouldBeInGroup = selectedDeviceIds.has(device.id);

        if (shouldBeInGroup && device.groupId !== targetGroupId) {
          // Adicionar ao grupo
          updates.push(
            fetch(`/api/devices/${device.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...device, groupId: targetGroupId }),
            }).then((r) => (r.ok ? r.json() : null)),
          );
        } else if (!shouldBeInGroup && wasInGroup) {
          // Remover do grupo (só no edit mode)
          updates.push(
            fetch(`/api/devices/${device.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...device, groupId: 0 }),
            }).then((r) => (r.ok ? r.json() : null)),
          );
        }
      });

      if (updates.length > 0) {
        const results = await Promise.all(updates);
        const updatedDevices = results.filter(Boolean);
        if (updatedDevices.length > 0) {
          dispatch(devicesActions.update(updatedDevices));
        }
      }

      onClose();
    } catch {
      // silently fail
    }
    setSaving(false);
  }, [groupName, isEdit, group, allDevices, selectedDeviceIds, dispatch, onClose]);

  // Contagem de selecionados vs filtrados
  const selectedCount = selectedDeviceIds.size;
  const filteredSelectedCount = deviceList.filter((d) => selectedDeviceIds.has(d.id)).length;
  const allFilteredSelected = deviceList.length > 0 && filteredSelectedCount === deviceList.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxHeight: '80vh',
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {t('groupObjectGroups') || 'Grupos de objetos'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        {/* Nome do grupo */}
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={t('groupNew') || 'Nome do grupo'}
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Toolbar: busca + select/deselect */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TextField
            size="small"
            placeholder={t('sharedSearch') || 'Buscar...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            size="small"
            startIcon={<SelectAll sx={{ fontSize: 16 }} />}
            onClick={handleSelectAll}
            disabled={allFilteredSelected}
            sx={{ textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
          >
            {t('selectAll') || 'Todos'}
          </Button>
          <Button
            size="small"
            startIcon={<Deselect sx={{ fontSize: 16 }} />}
            onClick={handleDeselectAll}
            disabled={filteredSelectedCount === 0}
            sx={{ textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
          >
            {t('deselectAll') || 'Nenhum'}
          </Button>
        </Box>

        {/* Contagem */}
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
          {selectedCount}
          {' '}
          {t('groupDevicesSelected') || 'dispositivos selecionados'}
          {search.trim() && ` (${deviceList.length} ${t('sharedVisible') || 'visíveis'})`}
        </Typography>

        {/* 2ECOMP: PremiumUI Refresh — grid 2 colunas igual GPSWOX */}
        <Box
          sx={{
            maxHeight: DEVICE_ITEM_HEIGHT * MAX_VISIBLE_DEVICES,
            overflow: 'auto',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 0.5,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.25 }}>
            {deviceList.map((device) => (
              <Box
                key={device.id}
                onClick={() => toggleDevice(device.id)}
                sx={{
                  height: DEVICE_ITEM_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  px: 0.5,
                  gap: 0.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedDeviceIds.has(device.id)}
                  sx={{ p: 0 }}
                  tabIndex={-1}
                />
                <Typography noWrap sx={{ fontSize: '0.75rem' }}>
                  {device.name}
                </Typography>
              </Box>
            ))}
          </Box>
          {deviceList.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('sharedNoResults') || 'Nenhum resultado'}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small" sx={{ textTransform: 'none' }}>
          {t('sharedCancel') || 'Cancelar'}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={saving || !groupName.trim()}
          sx={{ textTransform: 'none' }}
        >
          {saving
            ? (t('sharedSaving') || 'Salvando...')
            : (t('sharedSave') || 'Salvar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupEditModal;
